<template>
  <div class="w-100">
    <div
      class="input-wrapper card flex-row align-items-end w-100 mb-0 border pr-25 shadow-none"
    >
      <b-form-textarea
        v-model="message"
        :placeholder="isLimitReached ? $t('postView.reply.limitReached') : $t('postView.reply.placeholder')"
        :disabled="loading || isLimitReached"
        maxlength="2500"
        rows="1"
        no-resize
        class="reply-textarea flex-grow-1 border-0 shadow-none bg-transparent mr-2"
        @keydown.enter.exact.prevent="sendReply"
      />

      <div class="d-flex align-items-center flex-shrink-0">
        <transition name="fade">
          <span
            v-show="message.trim().length > 0"
            class="counter"
            :class="counterClass"
          >
            {{ charactersRemaining }}
          </span>
        </transition>

        <b-btn
          v-b-tooltip="{ title: $t('postView.sendReply'), placement: 'top', trigger: 'hover' }"
          :disabled="message.trim().length < 1 || loading || isLimitReached"
          class="border-0 rounded-circle d-flex align-items-center justify-content-center p-0 reply-btn"
          @click="sendReply"
        >
          <feather-icon
            icon="SendIcon"
            size="16"
            class="text-white"
            style="margin-left: -2px"
          />
        </b-btn>
      </div>
    </div>
  </div>
</template>
<script>
import DigitalRepliesService from '@/services/DigitalRepliesService';

const MAX_REPLIES_PER_POST = 1

export default {
  components: {},
  props: {
    post: {
      required: true,
      type: Object,
    },
    replies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      message: '',
      loading: false,
      maxRepliesPerPost: MAX_REPLIES_PER_POST,
    };
  },
  computed: {
    isLimitReached() {
      return this.replies.length >= MAX_REPLIES_PER_POST;
    },
    charactersRemaining() {
      const maxLength = 2500;
      return maxLength - this.message.length;
    },
    // Helper function to apply colours based on remaining characters
    counterClass() {
      if (this.charactersRemaining <= 0) {
        return 'counter-danger';
      }
      if (this.charactersRemaining < 100) {
        return 'counter-warning';
      }
      return 'text-body';
    },
  },
  methods: {
    sendReply() {
      if (this.message.trim().length < MAX_REPLIES_PER_POST || this.loading || this.isLimitReached) return;
      this.loading = true;

      // eslint-disable-next-line no-underscore-dangle
      DigitalRepliesService.sendReply(this.message, this.post._id).then(() => {
        this.$toast.success(this.$t('postView.reply.success'), {
          toastClassName: ['toast-std', 'success-toast'],
        });
        this.$root.$emit('refresh-post-list');
        this.$emit('reply-sent');
        this.message = ''; // reset message
      }).catch(err => {
        let msg = this.$t('postView.reply.errorDefault');
        const errorFound = err && err.response && err.response.data;

        if (errorFound && err.response.data.error === 'ENTITY_ALREADY_EXISTS') {
          msg = this.$t('postView.reply.errorAlreadyExists');
        }

        this.$toast.error(msg, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.loading = false;
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  padding-top: 4px;
  padding-bottom: 4px;
  border-radius: 24px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus-within {
    border-color: #6f427b;
    box-shadow: 0 0 0 0.15rem rgba(111, 66, 123, 0.25);
  }
}

.reply-textarea {
  field-sizing: content;
  flex: 1 1 0 !important;
  width: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: calc(1.5em * 8 + 1rem + 2px);
  padding-top: 0.4rem !important;
  padding-bottom: 0.6rem !important;
  line-height: 1.5;
  resize: none;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(111, 66, 123, 0.4) transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(111, 66, 123, 0.4);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(111, 66, 123, 0.6);
  }
}


.counter {
  display: inline-block;
  min-width: 4ch;
  margin-right: 12px;
  line-height: 1;
  font-size: 0.8rem;
  font-weight: 500;
  text-align: right;
}

.counter-danger {
  color: #ea5455 !important;
}
.counter-warning {
  color: #ff9f43 !important;
}

.reply-btn {
  width: 38px;
  height: 38px;
  background-color: #6f427b !important;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #8a5399 !important;
  }

  &:disabled {
    opacity: 1 !important;
    background-color: rgba(130, 134, 139, 0.15) !important;
    color: rgba(130, 134, 139, 0.5) !important;

    .feather-icon {
      color: rgba(130, 134, 139, 0.5) !important;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
